import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MathJaxContext, MathJax } from 'better-react-mathjax';
import { useAuth0 } from '@auth0/auth0-react';
import config from './config';

interface Exam {
  id: number;
  name: string;
  description: string | null;
  timeLimitMinutes: number;
  isActive: boolean;
}

interface ExamQuestion {
  id: number;
  examId: number;
  questionId: number;
  questionText: string;
  questionLatex?: string | null;
  optionA?: string | null;
  optionB?: string | null;
  optionC?: string | null;
  optionD?: string | null;
  optionE?: string | null;
  correctOption?: string | null;
  categoryId: number;
  difficultyLevel: number;
  isEnabled: boolean;
  orderIndex: number | null;
  marks: number;
  negativeMarks: number;
}

interface ExamAttemptStartResponse {
  id: number;
}

interface ExamAttemptResult {
  id: number;
  examId: number;
  examName: string;
  attemptNumber: number;
  status: string;
  timeSpentSeconds: number;
  totalQuestions: number;
  attemptedQuestions: number;
  correctCount: number;
  incorrectCount: number;
  rawScore: number;
  totalMarks: number;
  percentage: number;
  isPassed: boolean;
}

const OptionMathText = React.memo(function OptionMathText({ markup }: { markup: string }) {
  return (
    <MathJax inline>
      <span dangerouslySetInnerHTML={{ __html: markup }} />
    </MathJax>
  );
});

const isValidMcqQuestion = (question: ExamQuestion): boolean => {
  const options = [question.optionA, question.optionB, question.optionC, question.optionD, question.optionE]
    .filter((option) => !!option && option.trim().length > 0);

  const correctOption = (question.correctOption || '').trim().toUpperCase();
  const hasValidCorrectKey = ['A', 'B', 'C', 'D', 'E'].includes(correctOption);

  return options.length >= 2 && hasValidCorrectKey;
};

export const ExamQuiz: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, getAccessTokenSilently, loginWithRedirect } = useAuth0();

  const [exam, setExam] = useState<Exam | null>(null);
  const [questions, setQuestions] = useState<ExamQuestion[]>([]);
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [attemptId, setAttemptId] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [serverResult, setServerResult] = useState<ExamAttemptResult | null>(null);

  useEffect(() => {
    const fetchExam = async () => {
      try {
        setLoading(true);
        setSubmitError(null);
        setServerResult(null);
        setAttemptId(null);
        const examId = Number(id);
        if (!examId) {
          setError('Invalid exam id');
          return;
        }

        const headers: HeadersInit = {};
        let accessToken: string | null = null;

        if (isAuthenticated) {
          try {
            accessToken = await getAccessTokenSilently();
            headers.Authorization = `Bearer ${accessToken}`;
          } catch (err) {
            // Continue with guest access if token retrieval fails.
            console.warn('Unable to get access token for exam request', err);
          }
        }

        const [examResponse, questionsResponse] = await Promise.all([
          fetch(`${config.apiBaseUrl}/api/exams/${examId}`, { headers }),
          fetch(`${config.apiBaseUrl}/api/exams/${examId}/questions`, { headers }),
        ]);

        if (!examResponse.ok) throw new Error('Failed to load exam');
        if (!questionsResponse.ok) throw new Error('Failed to load exam questions');

        const examData: Exam = await examResponse.json();
        const examQuestions: ExamQuestion[] = await questionsResponse.json();

        const enabledQuestions = examQuestions
          .filter((q) => q.isEnabled)
          .filter(isValidMcqQuestion)
          .sort((a, b) => (a.orderIndex ?? Number.MAX_SAFE_INTEGER) - (b.orderIndex ?? Number.MAX_SAFE_INTEGER));

        setExam(examData);
        setQuestions(enabledQuestions);
        setSecondsLeft((examData.timeLimitMinutes || 60) * 60);

        if (isAuthenticated && accessToken) {
          try {
            const startResponse = await fetch(`${config.apiBaseUrl}/api/examattempts/start`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${accessToken}`,
              },
              body: JSON.stringify({ examId }),
            });

            if (startResponse.ok) {
              const attemptData: ExamAttemptStartResponse = await startResponse.json();
              setAttemptId(attemptData.id);
            } else {
              console.warn('Failed to start exam attempt. Results will be local only.');
            }
          } catch (attemptErr) {
            console.warn('Failed to start exam attempt. Results will be local only.', attemptErr);
          }
        }

        setError(null);
      } catch (err) {
        console.error(err);
        setError('Failed to load exam');
      } finally {
        setLoading(false);
      }
    };

    fetchExam();
  }, [id, isAuthenticated, getAccessTokenSilently]);

  useEffect(() => {
    if (loading || submitted || secondsLeft <= 0 || isSubmitting) return;

    const timer = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [loading, submitted, secondsLeft, isSubmitting]);

  const currentQuestion = questions[currentQuestionIndex];
  const hasAnsweredCurrentQuestion = !!answers[currentQuestion?.questionId];
  const selectedAnswer = currentQuestion ? answers[currentQuestion.questionId] : undefined;

  const options = useMemo(() => {
    if (!currentQuestion) return [] as Array<{ key: string; value: string }>;

    const rawOptions = [
      { key: 'A', value: currentQuestion.optionA || '' },
      { key: 'B', value: currentQuestion.optionB || '' },
      { key: 'C', value: currentQuestion.optionC || '' },
      { key: 'D', value: currentQuestion.optionD || '' },
      { key: 'E', value: currentQuestion.optionE || '' },
    ];

    return rawOptions.filter((opt) => opt.value.trim().length > 0);
  }, [currentQuestion]);

  const formatTime = (totalSeconds: number) => {
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const prepareForMathJax = (value?: string | null): string => {
    const text = (value || '').trim();
    if (!text) return '';

    const hasDelimiters = /\$|\\\(|\\\[/.test(text);
    const looksLikeLatex = /\\[a-zA-Z]+|\^|_/.test(text);

    if (!hasDelimiters && looksLikeLatex) {
      return `\\(${text}\\)`;
    }

    return text;
  };

  const toHtmlMarkup = (value?: string | null): string => {
    const prepared = prepareForMathJax(value);
    if (!prepared) return '';

    const hasHtmlTags = /<\/?[a-z][\s\S]*>/i.test(prepared);
    if (hasHtmlTags) {
      return prepared;
    }

    const escaped = prepared
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

    return escaped.replace(/\r?\n/g, '<br/>');
  };

  const questionMarkup = useMemo(() => {
    const source = (currentQuestion?.questionLatex || '').trim().length > 0
      ? currentQuestion?.questionLatex
      : currentQuestion?.questionText;

    return toHtmlMarkup(source);
  }, [currentQuestion?.questionId, currentQuestion?.questionLatex, currentQuestion?.questionText]);

  const optionMarkupMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const option of options) {
      map[option.key] = toHtmlMarkup(option.value);
    }
    return map;
  }, [options]);

  const computeResult = () => {
    const totalQuestions = questions.length;
    const attempted = questions.filter((q) => !!answers[q.questionId]).length;

    let correct = 0;
    let incorrect = 0;
    let score = 0;
    let totalMarks = 0;

    questions.forEach((q) => {
      totalMarks += q.marks;
      const selected = (answers[q.questionId] || '').trim().toUpperCase();
      const correctOption = (q.correctOption || '').trim().toUpperCase();

      if (!selected) return;

      if (selected === correctOption) {
        correct += 1;
        score += q.marks;
      } else {
        incorrect += 1;
        score -= q.negativeMarks;
      }
    });

    return {
      totalQuestions,
      attempted,
      correct,
      incorrect,
      score,
      totalMarks,
      percentage: totalMarks > 0 ? (score / totalMarks) * 100 : 0,
    };
  };

  const handleSubmitExam = async () => {
    if (submitted || isSubmitting) {
      return;
    }

    if (!isAuthenticated || !attemptId || !exam) {
      setSubmitted(true);
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const token = await getAccessTokenSilently();
      const timeSpentSeconds = Math.max(0, (exam.timeLimitMinutes || 60) * 60 - secondsLeft);

      const payload = {
        timeSpentSeconds,
        answers: questions.map((q) => ({
          questionId: q.questionId,
          selectedOption: answers[q.questionId] || null,
          timeSpentSeconds: null,
        })),
      };

      const response = await fetch(`${config.apiBaseUrl}/api/examattempts/${attemptId}/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err?.message || 'Failed to save exam attempt');
      }

      const result: ExamAttemptResult = await response.json();
      setServerResult(result);
      setSubmitted(true);
    } catch (submitErr) {
      console.error(submitErr);
      setSubmitError(submitErr instanceof Error ? submitErr.message : 'Failed to save exam attempt');
      setSubmitted(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (!loading && !submitted && secondsLeft === 0 && questions.length > 0) {
      handleSubmitExam();
    }
  }, [loading, submitted, secondsLeft, questions.length]);

  const localResult = computeResult();
  const result = serverResult
    ? {
        totalQuestions: serverResult.totalQuestions,
        attempted: serverResult.attemptedQuestions,
        correct: serverResult.correctCount,
        incorrect: serverResult.incorrectCount,
        score: serverResult.rawScore,
        totalMarks: serverResult.totalMarks,
        percentage: serverResult.percentage,
      }
    : localResult;

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !exam) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="max-w-3xl mx-auto bg-white rounded-lg shadow p-6">
          <div className="text-red-700 font-semibold mb-4">{error || 'Exam not found'}</div>
          <button
            onClick={() => navigate(-1)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="max-w-3xl mx-auto bg-white rounded-lg shadow p-6">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">{exam.name}</h1>
          <div className="text-gray-700 mb-4">No valid multiple-choice questions found for this exam.</div>
          <button
            onClick={() => navigate(-1)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded"
          >
            Back to Exams
          </button>
        </div>
      </div>
    );
  }

  return (
    <MathJaxContext>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
        <div className="max-w-5xl mx-auto">
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <div className="flex flex-wrap gap-4 items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-800">{exam.name}</h1>
                <p className="text-gray-600 mt-1">{exam.description || 'Timed multiple-choice exam'}</p>
              </div>
              <div className={`text-xl font-bold px-4 py-2 rounded ${secondsLeft <= 60 ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>
                ⏱️ {formatTime(secondsLeft)}
              </div>
            </div>

            <div className="mt-4 w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full"
                style={{ width: `${((currentQuestionIndex + 1) / questions.length) * 100}%` }}
              />
            </div>
            <div className="mt-2 text-sm text-gray-600">Question {currentQuestionIndex + 1} of {questions.length}</div>
          </div>

          {submitted ? (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Exam Result</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-gray-700">
                <div><strong>Total Questions:</strong> {result.totalQuestions}</div>
                <div><strong>Attempted:</strong> {result.attempted}</div>
                <div><strong>Correct:</strong> {result.correct}</div>
                <div><strong>Incorrect:</strong> {result.incorrect}</div>
                <div><strong>Score:</strong> {result.score.toFixed(2)} / {result.totalMarks.toFixed(2)}</div>
                <div><strong>Percentage:</strong> {result.percentage.toFixed(2)}%</div>
              </div>

              {isAuthenticated && serverResult && (
                <div className="mt-6 rounded-xl border border-green-200 bg-green-50 px-4 py-4">
                  <p className="text-sm font-semibold text-green-800">Results saved successfully</p>
                  <p className="text-xs text-green-700 mt-0.5">Attempt #{serverResult.attemptNumber} is now available for reporting and review.</p>
                </div>
              )}

              {isAuthenticated && !serverResult && (
                <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-4">
                  <p className="text-sm font-semibold text-amber-800">Results not fully saved</p>
                  <p className="text-xs text-amber-700 mt-0.5">{submitError || 'Attempt could not be persisted this time. Showing local result only.'}</p>
                </div>
              )}

              {!isAuthenticated && (
                <div className="mt-6 rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-4 flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-indigo-800">💾 Results not saved</p>
                    <p className="text-xs text-indigo-600 mt-0.5">Login or sign up to save your results and unlock all practice exams.</p>
                  </div>
                  <button
                    onClick={() =>
                      loginWithRedirect({
                        appState: {
                          returnTo: `${window.location.pathname}${window.location.search}${window.location.hash}`,
                        },
                      })
                    }
                    className="shrink-0 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-indigo-700"
                  >
                    Login / Sign Up
                  </button>
                </div>
              )}

              <div className="mt-6 flex gap-3">
                <button
                  onClick={() => navigate(-1)}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded"
                >
                  Back to Exams
                </button>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow p-6">
              <div className="mb-6 text-gray-800 leading-relaxed text-left text-xl md:text-2xl">
                <div className="flex items-start gap-2">
                  <span className="shrink-0 text-sm font-semibold text-blue-700 pt-0.5">Q{currentQuestionIndex + 1}.</span>
                  <MathJax key={currentQuestion.questionId}>
                    <div className="min-w-0" dangerouslySetInnerHTML={{ __html: questionMarkup }} />
                  </MathJax>
                </div>
              </div>

              <div className="space-y-3">
                {options.map((option) => {
                  const isSelected = selectedAnswer === option.key;
                  return (
                    <label
                      key={option.key}
                      className={`flex items-start gap-3 border rounded p-3 cursor-pointer transition-colors ${
                        isSelected
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="radio"
                        name={`question-${currentQuestion.questionId}`}
                        value={option.key}
                        checked={isSelected}
                        onChange={() =>
                          setAnswers((prev) => ({
                            ...prev,
                            [currentQuestion.questionId]: option.key,
                          }))
                        }
                      />
                      <div className="text-left text-gray-800">
                        <strong>{option.key}.</strong>{' '}
                        <OptionMathText markup={optionMarkupMap[option.key] || ''} />
                      </div>
                    </label>
                  );
                })}
              </div>

              <div className="mt-6 flex items-center justify-between gap-3">
                <button
                  onClick={() => setCurrentQuestionIndex((prev) => Math.max(0, prev - 1))}
                  disabled={currentQuestionIndex === 0}
                  className="bg-gray-600 hover:bg-gray-700 disabled:bg-gray-300 text-white px-4 py-2 rounded"
                >
                  Previous
                </button>

                {currentQuestionIndex === questions.length - 1 ? (
                  <button
                    onClick={handleSubmitExam}
                    disabled={isSubmitting}
                    className="bg-green-600 hover:bg-green-700 disabled:bg-gray-300 text-white px-4 py-2 rounded"
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Exam'}
                  </button>
                ) : (
                  <button
                    onClick={() => setCurrentQuestionIndex((prev) => Math.min(questions.length - 1, prev + 1))}
                    disabled={!hasAnsweredCurrentQuestion}
                    className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white px-4 py-2 rounded"
                  >
                    Next
                  </button>
                )}
              </div>

              <div className="mt-4 flex justify-center border-t border-gray-100 pt-4">
                <button
                  onClick={() => setShowExitConfirm(true)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-600 shadow-sm transition-colors hover:border-red-300 hover:bg-red-50 hover:text-red-600"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  Exit Exam
                </button>
              </div>

              {/* Custom Exit Confirm Dialog */}
              {showExitConfirm && (
                <div
                  className="fixed inset-0 z-50 flex items-center justify-center"
                  style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}
                  onClick={() => setShowExitConfirm(false)}
                >
                  <div
                    className="mx-4 w-full max-w-sm rounded-2xl bg-white shadow-2xl"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="px-6 pt-6 pb-2 flex items-start gap-4">
                      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-red-100">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                        </svg>
                      </div>
                      <div>
                        <h3 className="text-base font-semibold text-gray-900">Exit Exam?</h3>
                        <p className="mt-1 text-sm text-gray-500">Your progress and answers will be lost. This cannot be undone.</p>
                      </div>
                    </div>
                    <div className="px-6 py-4 flex justify-end gap-3">
                      <button
                        onClick={() => setShowExitConfirm(false)}
                        className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50"
                      >
                        Stay
                      </button>
                      <button
                        onClick={() => navigate(-1)}
                        className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-red-700"
                      >
                        Yes, Exit
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </MathJaxContext>
  );
};
